import type { Schema as JSONSchema } from '@cfworker/json-schema';
import { MESSAGE_DIRECTION_KEYS } from '../../sales/pages/types';
import { AsyncJobStatusResponse } from '../utils/asyncJobs';

export enum DiscountType {
  SHOPIFY_DISCOUNT_CODE = 'shopify-discount-code',
  FONDUE_CASHBACK_URL = 'fondue-cashback-promotion',
}

/* eslint-disable camelcase */
export interface ConfigValues {
  isLiveMode: boolean;
  discountType: DiscountType;
  discountCode?: string;
  discountFondueUrl?: string;
  discountDescription?: string;
  discountFondueDescription?: string;
  excludedProductTags: string[];
  fallbackSMS: string;
  supportEmail: string;
  supportSMS: string;
  outreachMessages: OutreachMessage[];
}

export interface ConfigDataMetadata {
  created_at?: string;
  first_go_live_at?: string;
  last_go_live_at?: string;
  last_deactivated_at?: string;
}

export interface ConfigData {
  created_at?: string;
  discount_code?: string;
  discount_description?: string;
  excluded_product_tags: string[];
  fallback_sms?: string;
  metadata?: ConfigDataMetadata;
  support_email: string;
  support_sms: string;
  is_live_mode: boolean;
  outreach_messages: OutreachMessage[];
  outreach_messages_modified?: boolean;
  exclusion_tags_modified?: boolean;
}

export interface Response {
  message: string;
}

export interface ShopperProductsStatusResponse {
  count_products_total: number;
  count_products_truncated: number;
  count_variants_truncated: number;
  is_truncated: boolean;
  has_full_product_access: boolean;
}

export interface ShopperProductsStatus {
  countProductsTotal: number;
  countProductsTruncated: number;
  countVariantsTruncated: number;
  isTruncated: boolean;
  hasFullProductAccess: boolean;
}

export enum ShopPromptOverridesProducts {
  SHOPPER_1_0 = 'Shopper 1.0',
  SHOPPER_2_0 = 'Shopper 2.0',
}

export enum FreePlayProducts {
  SHOPPER = 'Shopper-Playground',
  SENDER = 'MAI-Sender-Playground',
}

export interface ShopperPromptOverrides {
  shop_id: number;
  created_at: string;
  updated_at: string;
  last_updated_by: number;
  enabled: boolean;
  champion_prompt_name: string;
  champion_prompt_environment: ShopperPromptOverrideEnvironment;
  contender_prompt_enabled: boolean;
  contender_prompt_name: string;
  contender_prompt_environment: ShopperPromptOverrideEnvironment;
  contender_shadow_percentage: number;
  contender_rollout_percentage: number;
  product: string;
}

export interface ShopPromptDictResponse {
  shopper_1_0: ShopperPromptOverrides;
  shopper_2_0: ShopperPromptOverrides;
}

export type UpdateShopperPromptOverrides = Omit<
  ShopperPromptOverrides,
  'shop_id' | 'created_at' | 'updated_at' | 'last_updated_by'
>;

export const ShopperPromptOverrideEnvironments = {
  CHAMPION: 'champion',
  CONTENDER: 'contender',
};

export type ShopperPromptOverrideEnvironment =
  typeof ShopperPromptOverrideEnvironments[keyof typeof ShopperPromptOverrideEnvironments];

export interface ShopperPromptOverridesResponse {
  data: ShopPromptDictResponse | null;
  message: string | null;
}

export interface Prompt {
  env: string;
  instructions: string;
  name: string | undefined;
  tokens?: number;
}

export interface ShopperDebugActivePromptsResponse {
  prompts: Prompt[];
  message?: string;
}

/** Metadata that describes a single resolver) */
export interface ResolverDefinition {
  /** Unique identifier used by the backend (e.g. "shop_name") */
  name: string;
  /** Human-readable label to show in the UI */
  label: string;
  /** Short description of what the resolver produces */
  description: string;
  /** List of required inputs */
  input_schema: JSONSchema;
}

/** Top-level payload returned by `useContextResolverDefinitions` */
export interface ContextResolverDefinitionsResponse {
  resolvers: ResolverDefinition[];
}

export interface ExecuteContextResolverResponse {
  name: string;
  result: unknown;
}

export enum RegenerationStatus {
  NOT_REQUIRED = 'not_required',
  AWAITING_USER_CONFIRMATION = 'awaiting_user_confirmation',
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

// Types for message categories
export type SSAIMessageCategory =
  | 'question.discovery.shopping'
  | 'question.discovery.objection'
  | 'question.offer.proposal'
  | 'question.offer.nudge'
  | 'question.offer.reminder';

// Enums for message and batch statuses
export enum OutreachMessageStatus {
  APPROVED = 'approved',
  DESELECTED = 'deselected',
  DELETED = 'deleted',
  PENDING = 'pending',
}

export enum OutreachMessageBatchStatus {
  ACTIVE = 'active',
  UNREVIEWED = 'unreviewed',
}

// Interface for batch data
export interface OutreachMessageBatchData {
  [key: string]: unknown;
}

/**
 * Represents a batch of outreach messages
 */
export interface OutreachMessageBatch {
  id: number;
  status: OutreachMessageBatchStatus;
  data: OutreachMessageBatchData;
  created_at: string;
  updated_at: string;
  messages: OutreachMessage[];
}

/**
 * Represents a list of outreach messages for selection updates
 */
export interface OutreachMessageList {
  outreach_messages: OutreachMessage[];
}

/**
 * Represents an outreach message in the system
 */
export interface OutreachMessage {
  id: number;
  body: string;
  category: SSAIMessageCategory;
  status: OutreachMessageStatus;
  created_at: string;
  updated_at: string;
}

export interface AssignProductPlanFreeTrialResponse {
  message: string;
}

// Shopper Debug/Playground Types
export interface ShopperDebugMessage {
  direction: keyof typeof MESSAGE_DIRECTION_KEYS;
  body: string;
  created_at: string;
}

export interface ShopperDebugConversation {
  shop_id: number;
  subscriber_id: number;
  messages: ShopperDebugMessage[];
}

export interface ShopperDebugRequest {
  run_async: boolean;
  conversation: ShopperDebugConversation;
  shopper_prompt_id: string | null;
  shopper_prompt_env: string;
  ssai_settings: Record<string, unknown>;
}

export interface ShopperDebugReply {
  body: string;
}

export interface ShopperDebugResponse {
  shopper_reply: ShopperDebugReply;
}

export type ShopperDebugAsyncStatusResponse = AsyncJobStatusResponse<{
  shopper_reply: ShopperDebugReply;
}>;
